import { useMemo } from 'react';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import type { DevDocsBylineConnection } from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';

import { useIncomingLinksData } from '../../store/incomingLinksBridge.ts';
import {
	usePageAgentViewers,
	type UsePageAgentViewersResult,
} from '../../hooks/usePageAgentViewers.ts';
import { usePagePeople } from '../../hooks/usePagePeople.ts';
import type { UsePagePeopleResult } from '../../hooks/usePagePeople.ts';
import { useRelatedContentData } from '../../store/relatedContentBridge.ts';
import { useDevDocsConnection } from '../../store/devDocsConnectionBridge.ts';
import { useOutgoingLinks } from '../../store/outgoingLinksBridge.ts';
import type { EnhancedSmartLink } from '../../utils/smartLinkTypes.ts';

export type SyncedFromData = {
	connection: DevDocsBylineConnection | null;
};

export type AppearsInData = {
	links: EnhancedSmartLink[];
	loading: boolean;
	loadingMore: boolean;
	hasNextPage: boolean;
	endCursor: string | null;
	loadMore: () => void;
	error?: ApolloErrorInterop | undefined;
};

export type LinksToData = {
	links: EnhancedSmartLink[];
};

export type RelatedContentData = {
	/** False when the related-content experiment is off, and the section is absent. */
	enabled: boolean;
	links: EnhancedSmartLink[];
	loading?: boolean;
	error?: ApolloErrorInterop | undefined;
};

export type ConnectionSectionsData = {
	syncedFrom: SyncedFromData;
	appearsIn: AppearsInData;
	linksTo: LinksToData;
	relatedContent: RelatedContentData;
	/**
	 * The people the graph connects to this page. Unlike the slices above these come from AGG
	 * Graph Store rather than the SmartCard client, so they carry their own loading and error.
	 */
	people: UsePagePeopleResult;
	agentViewers: UsePageAgentViewersResult;
};

/**
 * Reads the byline's independent connection streams without fetching links again.
 * People and agent viewers are fetched by the panel; the modal receives this same assembled data object.
 */
export const useConnectionSectionsData = (contentId?: string): ConnectionSectionsData => {
	const { devDocsConnection } = useDevDocsConnection(contentId);
	const { incomingLinks: incoming } = useIncomingLinksData(contentId);
	const {
		links: incomingLinks,
		loading,
		loadingMore,
		hasNextPage,
		endCursor,
		loadMore,
		error: incomingError,
	} = incoming;
	const { outgoingLinks } = useOutgoingLinks(contentId);
	const { relatedContent } = useRelatedContentData(contentId);
	const {
		enabled: relatedContentEnabled,
		links: relatedContentLinks,
		loading: relatedContentLoading,
		error: relatedContentError,
	} = relatedContent;
	const people = usePagePeople(contentId);
	const agentViewers = usePageAgentViewers(contentId ?? '');

	return useMemo(
		() => ({
			syncedFrom: { connection: devDocsConnection },
			appearsIn: {
				links: incomingLinks,
				loading,
				loadingMore,
				hasNextPage,
				endCursor,
				loadMore,
				error: incomingError,
			},
			linksTo: { links: outgoingLinks },
			relatedContent: {
				enabled: relatedContentEnabled,
				links: relatedContentLinks,
				loading: relatedContentLoading,
				error: relatedContentError,
			},
			people,
			agentViewers,
		}),
		[
			devDocsConnection,
			incomingLinks,
			loading,
			loadingMore,
			hasNextPage,
			endCursor,
			loadMore,
			incomingError,
			outgoingLinks,
			relatedContentEnabled,
			relatedContentLinks,
			relatedContentLoading,
			relatedContentError,
			people,
			agentViewers,
		],
	);
};
